import { Appliance, ApplianceConfigRelay } from './Appliance'
import { Box } from '@mui/material'
import { Trans } from 'react-i18next'
import { CustomSwitch } from '../../components/atoms/CustomSwitch'
import { Dropdown } from '../../components/atoms/Dropdown'
import { RelayAppliance } from './RelayAppliance'
import { Text } from '../../components/atoms/Text'
import { ID } from '../../types/models'

export class TemperatureRelayAppliance<ConfigType extends ApplianceConfigRelay = ApplianceConfigRelay> extends RelayAppliance<ConfigType> {

  hasAdvanced() {
    return true
  }

  getDefaultConfig(): Partial<ApplianceConfigRelay> {
    return {
      use_temperature: false,
    }
  }

  drawSelectors(appliances: Appliance[]): React.ReactNode {
    const ctrl = this.getOptimizerControl()

    const useTemperature = !!this.getConfig('use_temperature')
    const portId: string = this.getConfig('temperature_port_id')
    const temperature = this.getConfig('temperature')
    const advanced = !!this.getConfig('advanced')

    const meters = appliances.filter(app => app.type === 'TEMPERATURE' && app.controls[0].port.port_number !== '104/0')
    const options: Record<string, string> = { }

    meters.forEach(m => (options[m.controls[0].port.id + ''] = m.getName()))

    return <Box>
      <Box sx={{ display: 'flex', mt: 2 }}>
        <CustomSwitch data-cy="switch-Use Temperature" disabled={meters.length === 0} onClick={() => this.setConfig('use_temperature', !useTemperature)} checked={!!useTemperature} sx={{ ml: '1rem' }}/>
        <Box sx={{ pt: 1, ml: 2 }}><Trans>Use temperature sensor to assist control</Trans></Box>
      </Box>
      {
        useTemperature && <>
          <Dropdown
            sx={{ ml: 2 }}
            oneLine
            label={this.app.t('Sensor to use')}
            items={options}
            selected={portId}
            onSelect={(v) => this.setConfig('temperature_port_id', parseInt(v || '0') || null)}
          />
          {
            !advanced && <Text
              sx={{ ml: 2, pt: 1, width: '6rem' }}
              oneLine
              unit="°C"
              disabled={!portId}
              label={this.app.t('Target temperature')}
              value={temperature || ''}
              onChange={(e) => this.setConfig('temperature', e.target.value)}
              onBlur={(e) => this.setConfig('temperature', parseInt(e.target.value || '0'))}
            />
          }
          {
            advanced && <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr' }}>
              <Text
                sx={{ pr: 1 }}
                unit="°C"
                disabled={!portId}
                label={this.app.t('abs. min')}
                value={ctrl.getConfig('abs_min_temp') || ''}
                onChange={(e) => ctrl.setConfig('abs_min_temp', e.target.value)}
                onBlur={(e) => ctrl.setConfig('abs_min_temp', parseInt(e.target.value || '0'))}
              />
              <Text
                sx={{ pr: 0.5 }}
                unit="°C"
                disabled={!portId}
                label={this.app.t('minimum')}
                value={ctrl.getConfig('min_temp') || ''}
                onChange={(e) => ctrl.setConfig('min_temp', e.target.value)}
                onBlur={(e) => ctrl.setConfig('min_temp', parseInt(e.target.value || '0'))}
              />
              <Text
                sx={{ pl: 0.5 }}
                unit="°C"
                disabled={!portId}
                label={this.app.t('maximum')}
                value={ctrl.getConfig('max_temp') || ''}
                onChange={(e) => ctrl.setConfig('max_temp', e.target.value)}
                onBlur={(e) => ctrl.setConfig('max_temp', parseInt(e.target.value || '0'))}
              />
              <Text
                sx={{ pl: 1 }}
                unit="°C"
                disabled={!portId}
                label={this.app.t('abs. max')}
                value={ctrl.getConfig('abs_max_temp') || ''}
                onChange={(e) => ctrl.setConfig('abs_max_temp', e.target.value)}
                onBlur={(e) => ctrl.setConfig('abs_max_temp', parseInt(e.target.value || '0'))}
              />
            </Box>
          }
        </>
      }
    </Box>
  }

  convert() {
    const useTemperature = this.getConfig('use_temperature')
    const portId: ID = this.getConfig('temperature_port_id')
    const temperature: number = this.getConfig('temperature') || 0
    const advanced = this.getConfig('advanced')
    const pvPriority = this.getConfig('pv_priority')
    const ctrl = this.getOptimizerControl()

    const ret = {}
    if (useTemperature && portId) {
      Object.assign(ret, {
        use_temperature_meas: true,
        tempsens_dest_kind: 'DEST_KIND_THIS_DEVICE',
        tempsens_dest: 0,
        tempsens_port: this.control.portControlForId(portId).port.port_number,
        min_temp: advanced ? ctrl.getConfig('min_temp') : temperature - 2,
        abs_min_temp: advanced ? ctrl.getConfig('abs_min_temp') : temperature - 4,
        max_temp: advanced ? ctrl.getConfig('max_temp') : temperature + 2,
        abs_max_temp: advanced ? ctrl.getConfig('abs_max_temp') : temperature + 4,
        pv_priority: pvPriority,
      })
    } else {
      Object.assign(ret, {
        use_temperature_meas: false
      })
    }
    Object.assign(ret, {
      pv_priority: pvPriority,
    })

    return ret
  }
}
